import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Package, Loader2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Detect ?from=admin once and stash a session flag so the portal layout
  // can show the "admin preview" banner across navigation within this tab.
  //
  // Also support ?impersonate_token=...&impersonate_user=<base64 JSON> — the
  // admin's "Login as Client" flow injects a pre-minted client JWT here so
  // the admin lands directly on /client/dashboard without needing the
  // client's password. The token is short-lived (30 min, server-issued).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('from') === 'admin') {
        sessionStorage.setItem('client_portal_from_admin', '1');
      }
      const token = params.get('impersonate_token');
      const userB64 = params.get('impersonate_user');
      if (token && userB64) {
        const user = JSON.parse(atob(decodeURIComponent(userB64)));
        localStorage.setItem('client_token', token);
        localStorage.setItem('client_user', JSON.stringify(user));
        toast.success(`Viewing portal as ${user.name || user.email || 'client'}`);
        navigate('/client/dashboard', { replace: true });
      }
    } catch (err) {
      console.warn('Impersonation payload could not be applied:', err);
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/client/auth/login`, { email, password });
      localStorage.setItem('client_token', res.data.access_token);
      localStorage.setItem('client_user', JSON.stringify(res.data.user));
      toast.success(`Welcome back, ${res.data.user.name}!`);
      navigate('/client/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-600/20 border border-purple-500/30 mb-4">
            <Package size={32} className="text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Client Portal</h1>
          <p className="text-purple-300/70 text-sm mt-1">Local Flyer Delivery</p>
        </div>

        <Card className="bg-white/5 backdrop-blur-xl border-white/10 p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-purple-400"
                data-testid="client-login-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-purple-400 pr-10"
                  data-testid="client-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white h-11"
              data-testid="client-login-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-xs">
              Don't have an account? Contact your delivery provider for access.
            </p>
          </div>
        </Card>

        <div className="text-center mt-6">
          <button onClick={() => navigate('/admin/login')} className="text-purple-400/50 hover:text-purple-300 text-xs transition-colors">
            Admin Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientLoginPage;
